import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import type { EquipmentView } from '@lims/shared-types';
import { settingsApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { StatusBadge, type Tone } from '../../components/ui/index.js';

/**
 * How an instrument's calibration validity reads at a glance (D278): expired
 * or missing is danger, due within the laboratory's notice period is warning.
 *
 * NOTHING HERE DECIDES WHETHER AN INSTRUMENT MAY BE USED. That is the server's
 * usability verdict, shown beside this. This is the date only, set against:
 *  - the server's own "today" for the laboratory (`usability.asOf`), never the
 *    browser's clock, so a laboratory in another time zone is not misread;
 *  - the laboratory's OWN notice period (`equipmentNoticeDays`, the setting the
 *    management dashboard counts "due soon" with), read at runtime and never
 *    restated here (CLAUDE.md section 6.1). A caller who may not read settings
 *    simply sees no "due soon" mark — never one computed from a guessed window.
 *
 * An empty valid-until date is "no calibration recorded", never "valid
 * forever" (CLAUDE.md section 8).
 */
export type CalibrationState = 'not_required' | 'none' | 'expired' | 'due_soon' | 'valid';

const TONE: Readonly<Record<CalibrationState, Tone>> = {
  not_required: 'neutral',
  none: 'danger',
  expired: 'danger',
  due_soon: 'warning',
  valid: 'success',
};

/** A calendar date (`YYYY-MM-DD`) moved by whole days, as a calendar date. */
function addDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number);
  const moved = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, date ?? 1));
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
}

export function calibrationState(
  equipment: Pick<EquipmentView, 'calibrationRequired' | 'calibrationValidUntil' | 'usability'>,
  noticeDays: number | null,
): CalibrationState {
  if (!equipment.calibrationRequired) return 'not_required';
  const validUntil = equipment.calibrationValidUntil;
  if (validUntil === null) return 'none';
  const today = equipment.usability.asOf;
  if (validUntil < today) return 'expired';
  if (noticeDays !== null && noticeDays > 0 && validUntil <= addDays(today, noticeDays)) {
    return 'due_soon';
  }
  return 'valid';
}

/** The laboratory's notice period, when this caller may read settings. */
export function useEquipmentNoticeDays(): number | null {
  const { can } = useAuth();
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
    retry: false,
  });
  const days = settings.data?.equipmentNoticeDays;
  return typeof days === 'number' ? days : null;
}

export function CalibrationBadge({ state }: { readonly state: CalibrationState }) {
  const { t } = useTranslation();
  return <StatusBadge label={t(`equipment.calibrationState.${state}`)} tone={TONE[state]} />;
}
